import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { io } from 'socket.io-client';
import { chatApi } from '../api/chat';
import type { MessageView } from '../api/chat.types';
import { API_BASE_URL, getAuthToken } from '../api/client';
import { isSocialMockEnabled } from '../featureFlags';

type MessageListener = (message: MessageView) => void;

interface ChatRealtimeValue {
  /** Tổng tin chưa đọc — badge trên tab Chat. */
  unread: number;
  setUnread: (unread: number) => void;
  /** Socket đang nối. false = màn đang mở phải polling dày hơn. */
  connected: boolean;
  /** Tăng mỗi khi có tin mới ở BẤT KỲ hội thoại nào — cho vào deps để danh sách chat tải lại. */
  messageTick: number;
  /** Nhận từng tin mới; trả về hàm huỷ đăng ký. */
  subscribe: (listener: MessageListener) => () => void;
}

/// Giá trị mặc định: tab bar vẫn render được khi tắt cờ social (không có Provider).
const ChatRealtimeContext = createContext<ChatRealtimeValue>({
  unread: 0,
  setUnread: () => undefined,
  connected: false,
  messageTick: 0,
  subscribe: () => () => undefined,
});

/// Polling dự phòng cho badge — xem lý do giữ polling khi socket đang nối ở ChatThreadScreen.
const UNREAD_POLL_MS = 30000;

/// Socket.IO nối vào gốc server, không qua tiền tố REST `/api` (main.ts setGlobalPrefix).
/// Bản deploy gọi REST bằng `/api` tương đối qua rewrite của Firebase Hosting, mà Hosting không
/// chuyển tiếp WebSocket — khi đó phải đặt VITE_SOCKET_URL trỏ thẳng Cloud Run, không thì chỉ polling.
function socketUrl(): string | null {
  const explicit = import.meta.env.VITE_SOCKET_URL?.trim();
  if (explicit) return `${explicit.replace(/\/$/, '')}/chat`;
  if (/^https?:\/\//.test(API_BASE_URL)) return `${API_BASE_URL.replace(/\/api\/?$/, '')}/chat`;
  return null;
}

export function ChatRealtimeProvider({ children }: { children: ReactNode }) {
  const [unread, setUnread] = useState(0);
  const [connected, setConnected] = useState(false);
  const [messageTick, setMessageTick] = useState(0);
  const listeners = useRef(new Set<MessageListener>());

  useEffect(() => {
    const url = socketUrl();
    if (isSocialMockEnabled() || !url) return;
    const socket = io(url, {
      transports: ['websocket'],
      // Dạng hàm: mỗi lần tự nối lại đọc token hiện tại.
      auth: (send) => send({ token: getAuthToken() }),
    });
    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('unread', (payload: { unread: number }) => setUnread(payload.unread));
    socket.on('message:new', (message: MessageView) => {
      setMessageTick((tick) => tick + 1);
      listeners.current.forEach((listener) => listener(message));
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    const refresh = () => {
      chatApi.getUnread().then(
        (result) => setUnread(result.unread),
        () => undefined, // Mất mạng tạm thời: giữ số cũ, lượt sau thử lại.
      );
    };
    refresh();
    const timer = setInterval(refresh, UNREAD_POLL_MS);
    return () => clearInterval(timer);
  }, []);

  const subscribe = useCallback((listener: MessageListener) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const value = useMemo(() => ({ unread, setUnread, connected, messageTick, subscribe }), [unread, connected, messageTick, subscribe]);

  return <ChatRealtimeContext.Provider value={value}>{children}</ChatRealtimeContext.Provider>;
}

export function useChatRealtime() {
  return useContext(ChatRealtimeContext);
}
